import React, { useEffect, useState } from 'react';

import AgentInfoContainer from 'flow/components/pbx/supervisor/sub-components/AgentInfoContainer';
import SupervisorAgentRowMenu from 'flow/components/pbx/supervisor/sub-components/SupervisorAgentRowMenu';

import { ExtensionKey } from 'flow/types/EntityKeys';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectExtensionActiveProfile,
    selectExtensionActiveProfileKey,
    selectExtensionStatus,
} from 'flow/selectors/ExtensionSelectors';
import { selectQueueMembersSoonLoggedInThreshold } from 'flow/selectors/SupervisorSelectors';
import { getQueueAgentProfileEndTimeExpiringSoon } from 'flow/utils/supervisor-utils';
import { twMerge } from 'tailwind-merge';
import { usePrevious } from 'flow/hooks';

interface Props {
    agentKey: ExtensionKey;
    onProfileOrEndTimeChanged: () => void;
}

const SupervisorAgentRow = ({ agentKey, onProfileOrEndTimeChanged }: Props) => {
    const [itemIsActive, setItemIsActive] = useState(false);
    const profileCurrent = useAppSelector((state) =>
        selectExtensionActiveProfile(state, agentKey),
    );

    const agentStatus = useAppSelector((state) =>
        selectExtensionStatus(state, agentKey),
    );

    const queueMembersSoonLoggedInThreshold = useAppSelector(
        selectQueueMembersSoonLoggedInThreshold,
    );

    const profileEndTime = getQueueAgentProfileEndTimeExpiringSoon(
        profileCurrent,
        queueMembersSoonLoggedInThreshold,
    );

    const activeProfileKey = useAppSelector((state) =>
        selectExtensionActiveProfileKey(state, agentKey),
    );

    const previousActiveProfileKey = usePrevious(activeProfileKey);
    const previousProfileEndTime = usePrevious(profileEndTime);

    useEffect(() => {
        if (
            profileEndTime !== previousProfileEndTime ||
            activeProfileKey !== previousActiveProfileKey
        ) {
            onProfileOrEndTimeChanged();
        }
    }, [
        profileEndTime,
        previousProfileEndTime,
        activeProfileKey,
        previousActiveProfileKey,
        onProfileOrEndTimeChanged,
    ]);

    return (
        <div
            key={`fragment-agent-${agentKey}`}
            className="inset-x-0 m-auto mb-1.5 flex max-w-2xl ps-3 pe-1"
        >
            <div className="hover:shadow-base group flex w-full items-center justify-between gap-3 rounded-sm bg-white px-3 py-3 shadow-xs shadow-black/5 dark:bg-gray-100">
                <div className="flex w-full">
                    <AgentInfoContainer
                        extensionKey={agentKey}
                        agentStatus={agentStatus}
                        profileCurrent={profileCurrent}
                        profileEndTime={profileEndTime}
                    />
                </div>
                <div
                    className={twMerge(
                        'items-center text-gray-800',
                        itemIsActive
                            ? 'flex'
                            : 'sr-only-until-hover-or-focus flex',
                    )}
                >
                    <SupervisorAgentRowMenu
                        agentKey={agentKey}
                        setActive={setItemIsActive}
                    />
                </div>
            </div>
        </div>
    );
};

export default React.memo(SupervisorAgentRow);
